import React from "react";
import {
  Alert,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { spacing } from "../../styles/theme";
import { SidebarItem } from "./SpecsEditorFields/index";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModel, useSpecsModelSelector } from "./SpecsModelContext";
import { getPositionStyleAssignments } from "./positionEditorModel";
import {
  createDefaultPositionSideForImage,
  createEmptyPosition,
  loadImageDimensions,
} from "./specsEditorUtils";

export default function SpecsPositionEditorSidebar() {
  const specsModel = useSpecsModel();
  const {
    editor: {
      identity: { productVersion },
      selectPositions,
    },
    selectedPositionId,
    globalPreviewComposite,
  } = useSpecsEditorContextValue();
  const currentVersion = useSpecsModelSelector(
    (model) => model.getVersion(productVersion) ?? null
  );
  const rawPositions = (currentVersion?.getPositions() ?? []).map(
    (position: any) => ({
      id: position.id,
      positionName: position.designState?.positionName ?? "",
    })
  );
  const styleAssignments = useSpecsModelSelector((model) =>
    getPositionStyleAssignments(model, productVersion)
  );

  const copyPositionBundle = React.useCallback(async () => {
    const payload = {
      positions: structuredClone(currentVersion?.positions ?? []),
      positionGroups: structuredClone(currentVersion?.positionGroups ?? []),
      stylePositionIds: Object.fromEntries(
        styleAssignments.map((assignment) => [
          assignment.styleKey,
          structuredClone(assignment.positionIds),
        ])
      ),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast.success("Copied positions to clipboard");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to copy positions to clipboard"
      );
    }
  }, [currentVersion, styleAssignments]);

  const pastePositionBundle = React.useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const parsed = JSON.parse(clipboardText);
      const payload = Array.isArray(parsed)
        ? {
            positions: parsed,
            positionGroups: [],
            stylePositionIds: {},
          }
        : {
            positions: Array.isArray(parsed?.positions) ? parsed.positions : [],
            positionGroups: Array.isArray(parsed?.positionGroups)
              ? parsed.positionGroups
              : [],
            stylePositionIds:
              parsed?.stylePositionIds &&
              typeof parsed.stylePositionIds === "object"
                ? parsed.stylePositionIds
                : {},
          };

      if (!Array.isArray(payload.positions)) {
        throw new Error("Clipboard does not contain a valid position bundle");
      }

      if (!currentVersion) {
        throw new Error("SpecsModel version is missing");
      }

      currentVersion.setPositionData({
        positions: payload.positions,
        positionGroups: payload.positionGroups,
        stylePositionIds: payload.stylePositionIds,
      });

      selectPositions(payload.positions[0]?.id);
      toast.success("Pasted positions from clipboard");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to paste positions from clipboard"
      );
    }
  }, [currentVersion, selectPositions]);

  const addPosition = React.useCallback(async () => {
    if (!currentVersion) {
      return;
    }

    const previewUrl =
      globalPreviewComposite.front.url ?? globalPreviewComposite.back.url;
    const sideName = globalPreviewComposite.front.url ? "front" : "back";
    const imageSize = await loadImageDimensions(previewUrl);
    const nextPosition = createEmptyPosition({
      side: createDefaultPositionSideForImage({
        sideName,
        imageSize,
      }),
    });
    currentVersion.addPosition(nextPosition);
    selectPositions(nextPosition.id);
  }, [currentVersion, globalPreviewComposite, selectPositions]);

  const selectPosition = React.useCallback(
    (positionId: string) => {
      selectPositions(positionId);
    },
    [selectPositions]
  );

  const deletePosition = React.useCallback(
    (positionId: string) => {
      if (!currentVersion?.removePosition(positionId)) {
        return;
      }

      currentVersion.removePositionIdFromStyleAssignments(positionId);
    },
    [currentVersion]
  );

  return (
    <Paper
      variant="outlined"
      sx={{ p: spacing.groupingDesktop, mb: spacing.groupingDesktop }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h2">Positions</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Copy all positions">
            <span>
              <IconButton
                size="small"
                onClick={copyPositionBundle}
                disabled={rawPositions.length === 0}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Paste positions">
            <span>
              <IconButton size="small" onClick={pastePositionBundle}>
                <ContentPasteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Add position">
            <span>
              <IconButton size="small" onClick={addPosition}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack spacing={0.5}>
        {rawPositions.map((position: any) => (
          <SidebarItem
            key={position.id}
            label={position.positionName || position.id}
            active={selectedPositionId === position.id}
            onClick={() => selectPosition(position.id)}
            onDelete={() => deletePosition(position.id)}
          />
        ))}
        {rawPositions.length === 0 && (
          <Alert severity="info">No positions imported yet.</Alert>
        )}
      </Stack>
    </Paper>
  );
}
